import { useSyncExternalStore } from 'react'
import { useSelector } from 'react-redux'

import { selectUser } from 'core-components/sitebuilder/store/selectors/selectUser'

import type {
  INetworkDebugRecorder,
  INetworkDebugRequest,
  INetworkDebugTiming,
  TNetworkDebugRecorderStart,
  TNetworkDebugRequestResult,
  TNetworkDebugRequestStart,
} from './types'

/** Payloads can be megabytes, so only the latest requests of the session are kept. */
export const NETWORK_DEBUG_REQUESTS_LIMIT = 10

const SUPER_ADMIN_ROLE = 'SuperAdmin'
const RESPONSE_TEXT_LIMIT = 4096

let requests: INetworkDebugRequest[] = []
let nextId = 1
const listeners = new Set<() => void>()

const emit = () => listeners.forEach((listener) => listener())

/** Requests are recorded only for a Super Admin: no one else can open them, and keeping the raw
 *  payloads costs memory. While a Super Admin simulates another user the roles are that user's,
 *  and nothing the server confirms tells the client who started the simulation. */
export const isNetworkDebugUser = (roleIds: readonly string[] | undefined) =>
  Boolean(roleIds?.includes(SUPER_ADMIN_ROLE))

/** For thunks: `selectIsNetworkDebugUser(getState())`. */
export const selectIsNetworkDebugUser = (state: Parameters<typeof selectUser>[0]) =>
  isNetworkDebugUser(selectUser(state)?.roleIds)

export const useIsNetworkDebugUser = () => useSelector(selectIsNetworkDebugUser)

export const startNetworkDebugRequest = (start: TNetworkDebugRequestStart): number => {
  const id = nextId++
  const isRepeat = requests.some((request) => request.name === start.name)
  const request: INetworkDebugRequest = {
    kind: isRepeat ? 'refetch' : 'load',
    silent: false,
    source: null,
    ...start,
    id,
    startedAt: Date.now(),
    state: 'pending',
    status: null,
    gzipBytes: null,
    jsonBytes: null,
    durationMs: null,
    timings: [],
    progress: null,
    raw: null,
    mapped: null,
    error: null,
  }

  requests = [...requests, request].slice(-NETWORK_DEBUG_REQUESTS_LIMIT)
  emit()

  return id
}

const patchRequest = (id: number, patch: Partial<INetworkDebugRequest>) => {
  if (!requests.some((request) => request.id === id)) {
    return
  }

  requests = requests.map((request) => (request.id === id ? { ...request, ...patch } : request))
  emit()
}

const durationOf = (timings: INetworkDebugTiming[] | undefined) =>
  timings?.length ? timings.reduce((sum, timing) => sum + timing.ms, 0) : null

export const finishNetworkDebugRequest = (id: number, result: TNetworkDebugRequestResult) =>
  patchRequest(id, {
    ...result,
    state: 'ok',
    progress: null,
    durationMs: durationOf(result.timings),
  })

export const failNetworkDebugRequest = (id: number, result: TNetworkDebugRequestResult) =>
  patchRequest(id, {
    ...result,
    state: 'error',
    progress: null,
    durationMs: durationOf(result.timings),
  })

export const reportNetworkDebugProgress = (
  id: number,
  loadedBytes: number,
  totalBytes: number | null
) => patchRequest(id, { progress: { loadedBytes, totalBytes } })

/** Adds a step measured after the request finished, such as the store update that follows it. */
export const appendNetworkDebugTiming = (id: number, timing: INetworkDebugTiming) => {
  const request = requests.find((item) => item.id === id)

  if (request) {
    const timings = [...request.timings, timing]
    patchRequest(id, { timings, durationMs: durationOf(timings) })
  }
}

/** Measures consecutive steps: each `step` records the time since the previous one. */
export const createNetworkDebugTimer = () => {
  const timings: INetworkDebugTiming[] = []
  let last = performance.now()

  return {
    timings,
    step(label: string) {
      const now = performance.now()
      timings.push({ label, ms: Math.round(now - last) })
      last = now
    },
  }
}

/** The start of a body that could not be parsed, as text. */
export const readNetworkDebugText = (buffer: ArrayBuffer | null | undefined) =>
  buffer ? new TextDecoder().decode(buffer.slice(0, RESPONSE_TEXT_LIMIT)) : null

const NOOP_RECORDER: INetworkDebugRecorder = {
  step: () => undefined,
  progress: () => undefined,
  finish: () => undefined,
  fail: () => undefined,
  measureStoreUpdate: () => undefined,
}

/** Plugs any request into the network data window: create it before the request, call `step`
 *  after each stage, then `finish` or `fail`. */
export const createNetworkDebugRecorder = ({
  enabled,
  ...start
}: TNetworkDebugRecorderStart): INetworkDebugRecorder => {
  if (!enabled) {
    return NOOP_RECORDER
  }

  const id = startNetworkDebugRequest(start)
  const timer = createNetworkDebugTimer()

  return {
    step: timer.step,
    progress: (loadedBytes, totalBytes) => reportNetworkDebugProgress(id, loadedBytes, totalBytes),
    finish: (result) => finishNetworkDebugRequest(id, { ...result, timings: timer.timings }),
    fail: ({ message, stage, responseText = null, ...result }) => {
      timer.step(stage)
      failNetworkDebugRequest(id, {
        ...result,
        timings: timer.timings,
        error: { message, stage, responseText },
      })
    },
    measureStoreUpdate: () => {
      const returnedAt = performance.now()
      setTimeout(() => {
        appendNetworkDebugTiming(id, {
          label: 'store',
          ms: Math.round(performance.now() - returnedAt),
        })
      }, 0)
    },
  }
}

/** Records a request made through a client that returns the parsed body: the body is the raw
 *  response, `map` gives what the caller keeps of it. */
export const recordNetworkDebugCall = async <TRaw, TResult = TRaw>(
  start: TNetworkDebugRecorderStart,
  request: () => Promise<TRaw>,
  map: (raw: TRaw) => TResult = (raw) => raw as unknown as TResult
): Promise<TResult> => {
  const recorder = createNetworkDebugRecorder(start)
  let stage = 'request'

  try {
    const raw = await request()
    recorder.step('request')
    stage = 'map'
    const result = map(raw)
    recorder.step('map')
    recorder.finish({ raw, mapped: result })

    return result
  } catch (error) {
    recorder.fail({ message: error instanceof Error ? error.message : String(error), stage })
    throw error
  }
}

export const getNetworkDebugRequests = () => requests

export const subscribeNetworkDebugRequests = (listener: () => void) => {
  listeners.add(listener)

  return () => {
    listeners.delete(listener)
  }
}

export const resetNetworkDebugRequests = () => {
  requests = []
  emit()
}

export const useNetworkDebugRequests = () =>
  useSyncExternalStore(subscribeNetworkDebugRequests, getNetworkDebugRequests)
